import React from 'react';
import { PropTypes } from 'prop-types';

import 'draft-js/dist/Draft.css';
import {
  Editor,
  EditorState,
  ContentState,
  getVisibleSelectionRect,
} from 'draft-js';

import { MAX_DISAMBIGUATION_MSG_LENGTH } from '@src/constants/MogamboConstants';
import { wereEntitiesToggled, wereSynonymsToggled } from './_utils';
import { getDecorators } from './_draft_decorators';

export default class UserResponseInput extends React.Component {
  state = {
    editorState: EditorState.createEmpty(),
    updatedSynonyms: {},
    updatedEntities: {},
  };

  componentDidMount() {
    const {
      value,
      synonyms = {},
      tags = [],
      domainTitle,
      metadata,
      entities = {},
      updatedSynonyms,
      updatedEntities,
    } = this.props;
    let { editorState } = this.state;
    const contentState = ContentState.createFromText(value);
    editorState = EditorState.push(editorState, contentState);

    let taggedSynonyms = synonyms;
    let taggedEntities = entities;

    if (Object.keys(updatedSynonyms).length) {
      taggedSynonyms = updatedSynonyms;
    }

    if (Object.keys(updatedEntities).length) {
      taggedEntities = updatedEntities;
    }

    editorState = EditorState.set(editorState, {
      decorator: getDecorators(
        taggedSynonyms,
        taggedEntities,
        tags,
        domainTitle,
        metadata
      ),
    });
    this.setState({ editorState });

    if (this._editor && this.props.editable && !this.props.noAutoFocus) {
      this._editor.focus();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.value !== nextProps.value) {
      this._updateEditorValue(nextProps);
    } else if (
      this.props.updatedSynonyms !== nextProps.updatedSynonyms &&
      JSON.stringify(nextProps.updatedSynonyms) !==
        JSON.stringify(this.state.updatedSynonyms)
    ) {
      this._updateEditorSynonym(nextProps);
    } else if (
      this.props.updatedEntities !== nextProps.updatedEntities &&
      JSON.stringify(nextProps.updatedEntities) !==
        JSON.stringify(this.state.updatedEntities)
    ) {
      this._updateEditorEntity(nextProps);
    }
  }

  render() {
    const { active, disabled, editable, readOnly } = this.props;
    return (
      <div
        className={`user-response-input ${disabled ? 'disabled' : ''} ${
          editable ? 'editable' : ''
        } ${!active ? 'inactive' : ''}`}
      >
        <Editor
          ref={(editor) => {
            this.props.editorRef(editor);
            if (editable && editor && !this._editor) {
              this._editor = editor;
            }
          }}
          readOnly={readOnly}
          editorState={this.state.editorState}
          onBlur={!editable && this._handleReturn}
          onChange={this._handleChange}
          handleBeforeInput={this._handleBeforeInput}
          handlePastedText={this._handlePastedText}
          handleReturn={
            this.props.enterForNewLine ? () => 'handled' : this._handleReturn
          }
          placeholder={this.props.placeholder}
        />
      </div>
    );
  }

  /**
   * Update editor state value
   * @param {Object} nextProps updated props
   */
  _updateEditorValue = (nextProps) => {
    let { editorState } = this.state;
    const contentState = ContentState.createFromText(nextProps.value);
    editorState = EditorState.push(editorState, contentState);
    editorState = EditorState.set(editorState, {
      decorator: getDecorators(
        nextProps.synonyms,
        nextProps.entities,
        nextProps.tags,
        nextProps.domainTitle,
        nextProps.metadata
      ),
    });
    this.setState({ editorState });
  };

  /**
   * Updates the user input with correct formatting
   * @param {Object} nextProps Updated props of the component
   */
  _updateEditorSynonym = (nextProps) => {
    const wereSynonymsToggledBool = wereSynonymsToggled(
      nextProps.value,
      nextProps.updatedSynonyms
    );

    if (wereSynonymsToggledBool) {
      requestAnimationFrame(() => {
        this.setState((prevState) => {
          let { editorState, updatedEntities } = prevState;
          const haveUpdatedEntities = Object.keys(updatedEntities).length
            ? updatedEntities
            : nextProps.updatedEntities;

          const entitiesOrUpdatedEntities = Object.keys(haveUpdatedEntities)
            .length
            ? haveUpdatedEntities
            : nextProps.entities;
          editorState = EditorState.set(editorState, {
            decorator: getDecorators(
              nextProps.updatedSynonyms,
              entitiesOrUpdatedEntities,
              nextProps.tags,
              nextProps.domainTitle,
              nextProps.metadata
            ),
          });
          return {
            editorState,
            updatedSynonyms: nextProps.updatedSynonyms,
          };
        });
      });
    }
  };

  /**
   * Updates the user input with correct formatting
   * @param {Object} nextProps Updated props of the component
   */
  _updateEditorEntity = (nextProps) => {
    const wereEntitiesToggledBool = wereEntitiesToggled(
      nextProps.entities,
      nextProps.updatedEntities
    );

    if (wereEntitiesToggledBool || this.props.onConnection) {
      requestAnimationFrame(() => {
        this.setState((prevState) => {
          let { editorState, updatedSynonyms } = prevState;
          const haveUpdatedSynonyms = Object.keys(updatedSynonyms).length
            ? updatedSynonyms
            : nextProps.updatedSynonyms;
          const synonymsOrUpdatedSynonyms = Object.keys(haveUpdatedSynonyms)
            .length
            ? haveUpdatedSynonyms
            : nextProps.synonyms;
          editorState = EditorState.set(editorState, {
            decorator: getDecorators(
              synonymsOrUpdatedSynonyms,
              nextProps.updatedEntities,
              nextProps.tags,
              nextProps.domainTitle,
              nextProps.metadata
            ),
          });
          return {
            editorState,
            updatedEntities: nextProps.updatedEntities,
          };
        });
      });
    }
  };

  _handleChange = (editorState) => {
    const {
      id,
      value,
      synonyms = {},
      metadata = {},
      entities = {},
      updatedEntities = {},
      updatedSynonyms = {},
      tags = [],
      active,
      onSelect,
      onDeselect,
      disableMultipleWords,
    } = this.props;

    const selectionState = editorState.getSelection();
    const anchorKey = selectionState.getAnchorKey();
    const currentContent = editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;
    const currentContentBlock = currentContent.getBlockForKey(anchorKey);
    const start = selectionState.getStartOffset();
    const end = selectionState.getEndOffset();
    const wholeText = currentContentBlock.getText();
    const selectedText = wholeText.slice(start, end);
    const visibleSelectionRect = getVisibleSelectionRect(window);
    const multipleWordsDisabled =
      disableMultipleWords && selectedText.split(' ').length > 1;

    // checks if updatedEntities is not an empty object, use that
    // otherwise use entities.
    const updatedEntitiesValue = Object.keys(updatedEntities).length
      ? updatedEntities
      : entities;
    const entitiesToBeDetailed = wereEntitiesToggled(
      entities,
      updatedEntitiesValue
    )
      ? updatedEntitiesValue
      : entities;
    const nodeEntitiesDetailed = Object.keys(entitiesToBeDetailed).map(
      (key) => {
        return {
          entity: key,
          active: entitiesToBeDetailed[key].active,
          words: [],
          id: entitiesToBeDetailed[key].id,
        };
      }
    );

    const userResponseEntities = metadata.entity_meta
      ? metadata.entity_meta
      : {};
    const userResponseEntitiesReverseMapped = Object.keys(
      userResponseEntities
    ).reduce(
      (r, k) =>
        Object.assign(r, {
          [userResponseEntities[k]]: (r[userResponseEntities[k]] || []).concat(
            k
          ),
        }),
      {}
    );

    Object.keys(userResponseEntitiesReverseMapped).forEach((userResponseEntity) => {
      nodeEntitiesDetailed.map((nodeEntityDetailed) => {
        if (
          nodeEntityDetailed.entity === userResponseEntity &&
          userResponseEntitiesReverseMapped[userResponseEntity]
        ) {
          userResponseEntitiesReverseMapped[userResponseEntity].map((word) => {
            nodeEntityDetailed.words.push(word);
          });
        }
      });
    });

    const isWord =
      (wholeText.slice(start - 1, start) === ' ' ||
        wholeText.slice(start - 1, start) === '' ||
        wholeText.slice(start, start + 1) === ' ') &&
      (wholeText.slice(end, end + 1) === ' ' ||
        wholeText.slice(end, end + 1) === '' ||
        wholeText.slice(end - 1, end) === ' ');

    const intersectingWords = tags.filter(
      (tag) =>
        tag !== selectedText.trim() &&
        (tag.includes(selectedText.trim()) || selectedText.trim().includes(tag))
    );

    if (
      visibleSelectionRect &&
      visibleSelectionRect.width > 1 &&
      !multipleWordsDisabled &&
      intersectingWords.length === 0 &&
      isWord
    ) {
      clearTimeout(this._timeout);
      // checks if updatedSynonyms is not an empty object, use that
      // otherwise use synonyms.
      const synonymsValue = Object.keys(updatedSynonyms).length
        ? updatedSynonyms
        : synonyms;
      const userResponse = {
        id,
        value,
        synonyms: synonymsValue,
        entities,
        nodeEntitiesDetailed,
        tags,
        active,
        metadata,
      };
      if (onSelect) {
        onSelect(selectedText, userResponse, visibleSelectionRect);
      }
    } else {
      clearTimeout(this._timeout);
      this._timeout = setTimeout(() => {
        if (onDeselect) {
          onDeselect();
        }
      }, 200);
    }

    if (this.props.is_disambiguation_suggestion) {
      this.props.updateCharLength(currentContentLength);
    }

    this.setState({ editorState });
  };

  _handleReturn = () => {
    if (this.props.editable) {
      // const contentState = ContentState.createFromText('');
      // const editorState = EditorState.push(this.state.editorState, contentState);
      const contentState = EditorState.createEmpty();
      const editorState = EditorState.moveFocusToEnd(contentState);
      this.setState({ editorState });
    }
    const text = this.state.editorState.getCurrentContent().getPlainText();
    if (text !== this.props.value) {
      return this.props.onReturn(text);
    }
    return 'handled';
  };

  /**
   * before input is registered with editor, this function is called
   * It checks if the input getting edited is a disambiguation response,
   * its total length shouldn't be more than 20 characters.
   */
  _handleBeforeInput = () => {
    const currentContent = this.state.editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;

    // "1" subtracted from disambiguation message length because index starts from 0
    if (
      this.props.is_disambiguation_suggestion &&
      currentContentLength > MAX_DISAMBIGUATION_MSG_LENGTH - 1
    ) {
      return 'handled';
    }
  };

  /**
   * on pasting a text in the editor, if the input is a disambiguation response
   * the pasted text should not have more than 20 characters
   * @param {string} pastedText text pasted in the input
   */
  _handlePastedText = (pastedText) => {
    const currentContent = this.state.editorState.getCurrentContent();
    const currentContentLength = currentContent.getPlainText('').length;

    if (
      this.props.is_disambiguation_suggestion &&
      currentContentLength + pastedText.length > MAX_DISAMBIGUATION_MSG_LENGTH
    ) {
      return 'handled';
    }
  };
}

UserResponseInput.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  is_disambiguation_suggestion: PropTypes.bool,
  domainTitle: PropTypes.string,
  editedData: PropTypes.arrayOf(PropTypes.object),
  enterForNewLine: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDeselect: PropTypes.func,
  onReturn: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  updatedEntities: PropTypes.shape({}),
  synonyms: PropTypes.arrayOf(PropTypes.object),
  tags: PropTypes.arrayOf(PropTypes.string),
  updatedSynonyms: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.string,
  editorRef: PropTypes.func,
  updateCharLength: PropTypes.func,
};

UserResponseInput.defaultProps = {
  active: true,
  disabled: false,
  is_disambiguation_suggestion: false,
  domainTitle: '',
  enterForNewLine: null,
  onDeselect: null,
  onReturn: () => {},
  onSelect: null,
  placeholder: 'Type what the user will say',
  readOnly: false,
  updatedEntities: {},
  updatedSynonyms: {},
  tags: [],
  value: '',
  editorRef: () => {},
  updateCharLength: () => {},
};
